import { fetchApi } from "@/config/api";
import {
  PromoCode,
  PromoCodeApi,
  CreatePromoCodeInput,
  PromoSourceRow,
} from "@/types/promo";
import { useAuthStore } from "@/stores/auth";

let sendPromoEmailInFlight = false;

const toPromoApi = (r: PromoSourceRow): PromoCodeApi => ({
  id_code_promo: r.id_remise,
  libelle: r.code,
  id_acheteur: r.id_acheteur,
  acheteurNom: r.acheteur?.nomSociete?.trim() || null,
  acheteurEmail: r.acheteur?.email || null,
  id_vendeur: r.id_vendeur,
  auteur: r.id_vendeur ? "vendeur" : "eFashion",
  type: r.type,
  montant: r.montant,
  minimum: r.montant_min,
  localisation: r.localisation != null ? String(r.localisation) : null,
  utilisation: String(r.utilisation ?? 0),
  actif: Number(r.actif) === 1,
  cumulable: Number(r.cumulable) === 1,
  date_creation: r.date_creation ?? null,
  date_debut: r.date_debut ?? null,
  date_fin: r.date_fin ?? null,
  supprimer: Number(r.supprimer),
  premiere_commande: Number(r.premiere_commande ?? 0) === 1,
  offert: r.type === "pourcentage" && Number(r.montant ?? 0) === 100,
});

// Fonctions API pour les codes promos
export async function getPromoCodes(
  vendeurId?: number,
): Promise<PromoCodeApi[]> {
  const query = `
    query GetCodePromos {
      remises {
        id_remise
        id_remise_type
        code
        titre
        id_acheteur
        acheteur {
          id_acheteur
          nomSociete
          email
        }
        id_vendeur
        type
        montant
        montant_min
        localisation
        utilisation
        actif
        cumulable
        premiere_commande
        supprimer
        date_creation
        date_debut
        date_fin
      }
    }
  `;

  const result = await fetchApi<{ remises: PromoSourceRow[] }>(query);

  const filteredCodes = result.remises.filter((r) => {
    // Filtre minimal pour éviter de masquer des lignes valides.
    const hasCode = !!String(r.code || "").trim();
    const isNotDeleted = Number(r.supprimer) === 0;
    const sameVendor = vendeurId
      ? Number(r.id_vendeur) === Number(vendeurId)
      : true;
    return hasCode && isNotDeleted && sameVendor;
  });
  return filteredCodes.map(toPromoApi);
}

export async function createPromoCode(
  input: CreatePromoCodeInput,
  vendeurId?: number,
): Promise<PromoCode> {
  const mutation = `
    mutation CreateCodePromo($input: CreateRemiseInput!) {
      createRemise(input: $input) {
        id_remise
        id_remise_type
        code
        titre
        id_acheteur
        acheteur {
          id_acheteur
          nomSociete
          email
        }
        id_vendeur
        type
        montant
        montant_min
        localisation
        utilisation
        actif
        cumulable
        premiere_commande
        date_creation
        date_debut
        date_fin
        supprimer
      }
    }
  `;

  const backendInput = {
    id_remise_type: 1,
    code: input.code,
    // Création avec titre puis neutralisation pour compatibilité affichage ancien BOV (titre IS NULL)
    titre: input.code,
    id_codepromo_old: null,
    id_acheteur: input.acheteur === "tous" ? null : parseInt(input.acheteur),
    id_vendeur:
      vendeurId ||
      Number(useAuthStore().user?.id_vendeur ?? useAuthStore().user?.id),
    type: input.type === "Percentage" ? "pourcentage" : "reduction",
    montant: parseFloat(input.montant.replace(/[€%]/g, "")),
    montant_min: parseFloat(input.montantMin),
    montant_max: 999999,
    pourcentage:
      input.type === "Percentage"
        ? Math.max(1, Math.round(parseFloat(input.montant.replace(/[€%]/g, ""))))
        : 1,
    localisation: parseInt(input.localisation),
    utilisation: input.utilisation === "unique" ? 1 : 0,
    actif: input.actif === "activé" ? 1 : 0,
    cumulable: input.cumulable ? 1 : 0,
    efashion: 0,
    premiere_commande: input.premiereCommande ? 1 : 0,
    date_debut: input.dateDebut ? new Date(input.dateDebut).toISOString() : null,
    date_fin: input.dateFin ? new Date(input.dateFin).toISOString() : null,
    supprimer: 0,
  };

  const result = await fetchApi<{ createRemise: PromoSourceRow }>(mutation, {
    input: backendInput,
  });
  let code = toPromoApi(result.createRemise);

  try {
    const updateMutation = `
      mutation UpdateCodePromo($input: UpdateRemiseInput!) {
        updateRemise(input: $input) {
          id_remise
          id_remise_type
          code
          titre
          id_acheteur
          id_vendeur
          type
          montant
          montant_min
          localisation
          utilisation
          actif
          cumulable
          premiere_commande
          date_creation
          date_debut
          date_fin
          supprimer
        }
      }
    `;
    const updated = await fetchApi<{ updateRemise: PromoSourceRow }>(
      updateMutation,
      { input: { id_remise: code.id_code_promo, titre: null } },
    );
    code = toPromoApi(updated.updateRemise);
  } catch {
    // Fallback: on conserve la création si le patch titre échoue.
  }

  if (!code) {
    throw new Error("Aucune donnée retournée par le serveur");
  }

  // Récupérer le nom de société de l'acheteur pour l'affichage (au lieu de l'ID)
  // Note: éviter l'annotation de type inferrable pour satisfaire eslint (@typescript-eslint/no-inferrable-types)
  let buyerName = "Tous les acheteurs";
  let buyerIdStr: string | null = null;
  let buyerEmail: string | null = null;
  try {
    if (code.id_acheteur) {
      // Charger la liste des acheteurs et retrouver le bon
      const buyersQuery = `
        query GetBuyersByVendorForCreatePromoLabel {
          acheteurs {
            id_acheteur
            nomSociete
            email
          }
        }
      `;
      const buyersRes = await fetchApi<{
        acheteurs: Array<{
          id_acheteur: string | number;
          nomSociete: string;
          email: string;
        }>;
      }>(buyersQuery);
      const match = buyersRes.acheteurs.find(
        (a) => String(a.id_acheteur) === String(code.id_acheteur),
      );
      if (match) {
        buyerName = match.nomSociete;
        buyerIdStr = String(match.id_acheteur);
        buyerEmail = match.email || null;
      } else {
        // Valeur de secours: afficher quand même l'ID si nom introuvable
        buyerName = String(code.id_acheteur);
        buyerIdStr = String(code.id_acheteur);
      }
    }
  } catch {
    // En cas d'erreur réseau, retomber sur une valeur de secours
    if (code.id_acheteur) {
      buyerName = String(code.id_acheteur);
      buyerIdStr = String(code.id_acheteur);
    }
  }

  // Fonction pour formater les dates
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Non définie";
    try {
      return new Date(dateString).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return "Date invalide";
    }
  };

  return {
    id: String(code.id_code_promo),
    code: code.libelle || "",
    acheteur: buyerName,
    idAcheteur: buyerIdStr,
    buyerEmail: buyerEmail,
    type: code.type === "pourcentage" ? "Percentage" : "Reduction",
    montant:
      code.type === "pourcentage" ? `${code.montant}%` : `${code.montant}€`,
    montantMin: code.minimum ? code.minimum.toString() : "0",
    localisation:
      Number(code.localisation) === 1 ? "France" : "International",
    utilisation: (Number(code.utilisation) === 1
      ? "unique"
      : "multiple") as "unique" | "multiple",
    actif: code.actif ? "activé" : "désactivé",
    dateDebut: formatDate(code.date_debut),
    dateFin: formatDate(code.date_fin),
    dateCreation: formatDate(code.date_creation),
    premiereCommande: !!code.premiere_commande,
    offert: !!code.offert,
    cumulable: !!code.cumulable,
  };
}

export async function deletePromoCode(id: string): Promise<boolean> {
  // Soft delete : mettre supprimer = 1 et actif = 0
  const mutation = `
    mutation UpdateCodePromo($input: UpdateRemiseInput!) {
      updateRemise(input: $input) {
        id_remise
        supprimer
        actif
      }
    }
  `;

  const result = await fetchApi<{
    updateRemise: {
      id_remise: number;
      supprimer: number;
      actif: number;
    };
  }>(mutation, {
    input: {
      id_remise: parseInt(id),
      supprimer: 1,
      actif: 0,
    },
  });
  return !!result.updateRemise;
}

// Envoi d'un email promotionnel basé sur le template Code Promo (backend GraphQL sendPromoEmail)
export async function sendPromoEmail(input: {
  to: string[];
  buyerFirstName?: string;
  sellerName: string;
  discountLabel: string;
  promoCode: string;
  endDate: string;
  catalogueUrl?: string;
}): Promise<boolean> {
  // Prevent duplicate rapid clicks: simple in-flight guard at module scope
  // Note: this only throttles concurrent calls during a short window
  // and resets immediately after the request finishes.
  if (sendPromoEmailInFlight) {
    return false;
  }
  sendPromoEmailInFlight = true;
  const mutation = `
    mutation SendPromoEmail($input: SendPromoEmailInput!) {
      sendPromoEmail(input: $input)
    }
  `;
  try {
    const res = await fetchApi<{ sendPromoEmail: boolean }>(mutation, {
      input,
    });
    return !!res.sendPromoEmail;
  } finally {
    sendPromoEmailInFlight = false;
  }
}

// Récupère l'aperçu HTML de l'email promo (ne déclenche pas d'envoi)
export async function getPromoEmailPreview(input: {
  buyerFirstName?: string;
  sellerName?: string;
  discountLabel?: string;
  promoCode?: string;
  endDate?: string;
  catalogueUrl?: string;
}): Promise<string> {
  const query = `
    query PromoEmailPreview($input: PromoEmailPreviewInput!) {
      promoEmailPreview(input: $input)
    }
  `;
  const res = await fetchApi<{ promoEmailPreview: string }>(query, { input });
  return res.promoEmailPreview;
}

export async function togglePromoCodeStatus(
  id: string,
  isActive: boolean,
): Promise<boolean> {
  const mutation = `
    mutation UpdateCodePromo($input: UpdateRemiseInput!) {
      updateRemise(input: $input) {
        id_remise
        actif
      }
    }
  `;

  const result = await fetchApi<{
    updateRemise: { id_remise: number; actif: number };
  }>(mutation, {
    input: {
      id_remise: parseInt(id),
      actif: isActive ? 1 : 0,
    },
  });
  return !!result.updateRemise;
}
